import { Fragment, useMemo } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { Description, MailOutline } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import { GridColDef } from "@mui/x-data-grid";
import CustomModal from "../../components/ui/modal/modal.component";
import CustomDataGrid from "../../components/ui/customDataGrid/customDataGrid.component";
import EntityReferenceLink from "../../components/ui/entityReference/entityReferenceLink.component";
import {
  EquipmentCountersSource,
  EquipmentCountersSourceEquipment,
  EquipmentCounterReading,
} from "../../types/analytics";
import { EQUIPMENT_COUNTERS_SOURCE_EQUIPMENT_ENDPOINT } from "../../consts";
import { formatCounterValue, formatDateTime } from "../../utils";

interface EquipmentCountersSourceModalProps {
  opened: boolean;
  setOpened: (value: boolean) => void;
  source: EquipmentCountersSource | null;
  sourceLabel: string;
  selectedMonth: Dayjs | null;
}

/** One ingestion: the e-mail (and PDF attachment) a set of readings was read
 * from, plus every reading that came out of it. A single report always yields
 * all its counters at once, so the origin belongs to the group, not to each
 * individual reading. */
interface ReadingGroup {
  key: string;
  mailLabel: string;
  fileName: string | null;
  readings: EquipmentCounterReading[];
  /** The readings' common read time, when they share one (the normal case -
   * one report, one Send Date); null when they disagree, in which case each
   * reading carries its own timestamp instead. */
  sharedReadAt: string | null;
  /** Newest read time in the group, used to list recent ingestions first. */
  latestReadAt: string;
}

const buildMailLabel = (reading: EquipmentCounterReading) =>
  [reading.source_mail_subject, reading.source_mail_uid ? `UID ${reading.source_mail_uid}` : null]
    .filter(Boolean)
    .join(" · ");

/** Collapse readings onto the ingestion they came from, most recent first.
 * Readings with no recorded origin (manual entries, and anything ingested
 * before the origin was tracked) fall together into one sourceless group. */
const groupReadingsBySource = (readings: EquipmentCounterReading[]): ReadingGroup[] => {
  const groups = new Map<string, ReadingGroup>();

  readings.forEach((reading) => {
    const key = [
      reading.source_mail_uid,
      reading.source_mail_subject,
      reading.source_file_name,
    ].join("|");
    const group = groups.get(key);

    if (group) {
      group.readings.push(reading);
      return;
    }

    groups.set(key, {
      key,
      mailLabel: buildMailLabel(reading),
      fileName: reading.source_file_name,
      readings: [reading],
      sharedReadAt: null,
      latestReadAt: reading.read_at,
    });
  });

  return Array.from(groups.values())
    .map((group) => {
      const readAts = group.readings.map((reading) => reading.read_at);
      return {
        ...group,
        sharedReadAt: readAts.every((readAt) => readAt === readAts[0]) ? readAts[0] : null,
        latestReadAt: readAts.reduce((latest, readAt) => (readAt > latest ? readAt : latest)),
      };
    })
    .sort((first, second) => second.latestReadAt.localeCompare(first.latestReadAt));
};

// Same date(+time) styling as the jobs list grid - bold date on top, smaller
// secondary-colored time below - so date columns look consistent across grids.
const DateTimeLines = ({ value }: { value: string }) => {
  const [date, time] = formatDateTime(value).split("\n");

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 12, lineHeight: 1.15 }}>{date}</Typography>
      {time && (
        <Typography sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1.15 }}>
          {time}
        </Typography>
      )}
    </Box>
  );
};

// One "where did this come from" line - an icon plus an ellipsized label, with
// the full text on hover (subjects and file names are routinely longer than
// the column).
const SourceLine = ({
  icon,
  label,
  tooltip,
}: {
  icon: React.ReactNode;
  label: string;
  tooltip: string;
}) => (
  <Tooltip title={tooltip} placement="top-start">
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      {icon}
      <Typography
        sx={{
          fontSize: 10,
          lineHeight: 1.3,
          color: "text.secondary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  </Tooltip>
);

/** The e-mail and attachment behind one group - the whole point of this grid
 * for the client: when a scanned report only yields some of the counters
 * (color but no black), this points straight at the message and the PDF to
 * re-check. */
const GroupSource = ({
  group,
  expectIngested,
}: {
  group: ReadingGroup;
  expectIngested: boolean;
}) => {
  const { t } = useTranslation();

  if (!group.mailLabel && !group.fileName) {
    return expectIngested ? (
      <Typography
        sx={{ fontSize: 10, lineHeight: 1.3, color: "text.disabled", fontStyle: "italic" }}
      >
        {t("Ingestion source not recorded")}
      </Typography>
    ) : null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
      {group.mailLabel && (
        <SourceLine
          icon={<MailOutline sx={{ fontSize: 12, color: "text.secondary", flexShrink: 0 }} />}
          label={group.mailLabel}
          tooltip={`${t("Source e-mail")}: ${group.mailLabel}`}
        />
      )}
      {group.fileName && (
        <SourceLine
          icon={<Description sx={{ fontSize: 12, color: "text.secondary", flexShrink: 0 }} />}
          label={group.fileName}
          tooltip={`${t("Source file")}: ${group.fileName}`}
        />
      )}
    </Box>
  );
};

// One reading per line - black, then color, then scanned (the order the API
// returns them in) - so a group missing a kind reads as a gap at a glance.
// Emits bare grid cells rather than its own row Box: the columns are sized by
// the whole group, which keeps the values aligned in a single right-hand
// column regardless of how long the translated kind names are.
const ReadingRow = ({
  reading,
  showReadAt,
}: {
  reading: EquipmentCounterReading;
  showReadAt: boolean;
}) => (
  <>
    {/* justifySelf overrides the grid's default `stretch`: without it every
        chip is blown up to the width of the longest kind name, leaving the
        short ones ("Czarne strony") swimming in empty space. */}
    <Chip
      label={reading.kind_display}
      size="small"
      variant="outlined"
      sx={{ justifySelf: "start" }}
    />
    <Typography sx={{ fontWeight: 600, fontSize: 12, justifySelf: "end" }}>
      {formatCounterValue(reading.value)}
    </Typography>
    {/* Always a cell, empty or not, so the rows stay in step. */}
    {showReadAt ? <DateTimeLines value={reading.read_at} /> : <Box />}
  </>
);

const ReadingGroupBlock = ({
  group,
  expectIngested,
}: {
  group: ReadingGroup;
  expectIngested: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      minWidth: 0,
      borderLeft: 2,
      borderColor: "divider",
      pl: 1.25,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0 }}>
      {group.sharedReadAt && <DateTimeLines value={group.sharedReadAt} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <GroupSource group={group} expectIngested={expectIngested} />
      </Box>
    </Box>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "max-content max-content max-content",
        justifyContent: "start",
        alignItems: "center",
        columnGap: 1,
        rowGap: 0.5,
      }}
    >
      {group.readings.map((reading, index) => (
        <Fragment key={`${reading.kind}-${reading.read_at}-${index}`}>
          <ReadingRow reading={reading} showReadAt={!group.sharedReadAt} />
        </Fragment>
      ))}
    </Box>
  </Box>
);

export const EquipmentCountersSourceModal = ({
  opened,
  setOpened,
  source,
  sourceLabel,
  selectedMonth,
}: EquipmentCountersSourceModalProps) => {
  const { t } = useTranslation();

  const extraParams = useMemo(
    () =>
      source && selectedMonth
        ? {
            source,
            settlements_year: selectedMonth.year(),
            settlements_month: selectedMonth.month() + 1,
          }
        : undefined,
    [source, selectedMonth]
  );

  // Only the two e-mail-ingested sources can have a mail/file origin at all;
  // for readings entered by users or clients there is nothing missing.
  const expectIngested = source === "pdf" || source === "email_report";

  const columns = useMemo<GridColDef<EquipmentCountersSourceEquipment>[]>(
    () => [
      {
        field: "contract_number",
        headerName: t("Contract number"),
        valueGetter: (_value, row) => row.contract_number || "-",
      },
      {
        field: "contract_internal_name",
        headerName: t("Internal name"),
        sortable: false,
        valueGetter: (_value, row) => row.contract_internal_name || "-",
      },
      {
        field: "name",
        headerName: t("Equipment"),
        sortable: false,
        flex: 1.2,
        renderCell: (params) => (
          <EntityReferenceLink
            entityType="equipment"
            entityId={params.row.id}
            label={params.row.name}
          />
        ),
      },
      {
        field: "counter_readings",
        headerName: t("Counter readings"),
        sortable: false,
        flex: 2.6,
        renderCell: (params) => {
          const groups = groupReadingsBySource(params.row.counter_readings || []);
          if (groups.length === 0) {
            return "-";
          }

          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, py: 1, minWidth: 0 }}
            >
              {groups.map((group) => (
                <ReadingGroupBlock
                  key={group.key}
                  group={group}
                  expectIngested={expectIngested}
                />
              ))}
            </Box>
          );
        },
      },
    ],
    [t, expectIngested]
  );

  return (
    <CustomModal
      opened={opened}
      setOpened={setOpened}
      title={sourceLabel}
      width="min(1100px, 95vw)"
      content={
        <Box sx={{ px: 2 }}>
          {opened && extraParams ? (
            <CustomDataGrid
              url={EQUIPMENT_COUNTERS_SOURCE_EQUIPMENT_ENDPOINT}
              gridSettingsKey="SETTLEMENTS_EQUIPMENT_COUNTERS_SOURCE"
              columns={columns}
              extraParams={extraParams}
              defaultPageSize={20}
              dataGridProps={{
                autoHeight: true,
                pageSizeOptions: [10, 20, 50],
                disableColumnFilter: true,
              }}
            />
          ) : null}
        </Box>
      }
    />
  );
};
